'use client';

import { useRef } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="hero-bg relative overflow-hidden min-h-screen flex flex-col justify-center"
      style={{ paddingTop: '72px' }}
    >
      {/* Grid overlay */}
      <div className="hero-grid-overlay absolute inset-0 pointer-events-none" />
      {/* Glow */}
      <div className="hero-glow absolute inset-0 pointer-events-none" />
      {/* Decorative circles */}
      <div
        className="absolute top-1/4 right-[-120px] w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{ background: 'rgba(59,95,191,0.18)', filter: 'blur(80px)' }}
      />
      <div
        className="absolute bottom-[-80px] left-[-80px] w-[320px] h-[320px] rounded-full pointer-events-none"
        style={{ background: 'rgba(31,58,138,0.25)', filter: 'blur(60px)' }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div style={{ animation: 'fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) forwards' }}>
            {/* Trust badge */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="trust-badge">
                <span className="trust-dot" />
                US Registered LLC — Wyoming
              </span>
              <span className="trust-badge">8+ Years of Excellence</span>
            </div>

            <h1
              className="hero-headline-gradient font-heading font-extrabold leading-tight mb-6"
              style={{ fontSize: 'clamp(2.6rem, 5vw, 4rem)', letterSpacing: '-0.03em' }}
            >
              Digital Solutions<br />
              Built for Global<br />
              Growth.
            </h1>

            <p
              className="text-lg leading-relaxed mb-10 max-w-xl"
              style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'Manrope, sans-serif' }}
            >
              Cartelix LLC is a US-registered digital agency delivering premium
              websites, mobile apps, social media content, and brand transformations
              to businesses worldwide — with transparent pricing and zero surprises.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <a href="#contact" className="btn-primary">
                Start Your Project
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
              <a href="#services" className="btn-secondary">
                Our Services
              </a>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap gap-6">
              {[
                { icon: '🏛️', label: 'CARTELIX LLC', sub: 'Wyoming, USA' },
                { icon: '🔒', label: 'SSL Secured', sub: 'Stripe & PayPal' },
                { icon: '🌍', label: 'International', sub: '240+ Projects' },
              ]?.map((item) => (
                <div key={item?.label} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.1)' }}
                  >
                    {item?.icon}
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#fff', fontFamily: '"DM Sans",sans-serif' }}>{item?.label}</p>
                    <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{item?.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Floating UI Cards */}
          <div
            className="hidden lg:flex flex-col items-end gap-6"
            style={{ animation: 'fadeIn 1s ease 0.4s both' }}
          >
            {/* Main card */}
            <div
              className="float-card w-full max-w-sm rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-brand-primary flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 9h6M9 13h4"/></svg>
                </div>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#fff', fontFamily: '"DM Sans",sans-serif' }}>Project Kickoff</p>
                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Discovery call scheduled</p>
                </div>
              </div>
              <div className="space-y-2">
                {['Requirements gathering', 'Wireframe design', 'Development sprint', 'QA & Testing', 'Launch']?.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: i < 2 ? '#1F3A8A' : 'rgba(255,255,255,0.1)' }}
                    >
                      {i < 2 ? (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                      ) : (
                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.3)' }} />
                      )}
                    </div>
                    <span style={{ fontSize: '13px', color: i < 2 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)' }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stat cards row */}
            <div className="flex gap-4 w-full max-w-sm">
              <div
                className="float-card-delay flex-1 rounded-2xl p-5 text-center"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}
              >
                <p style={{ fontSize: '28px', fontWeight: 800, color: '#fff', fontFamily: '"DM Sans",sans-serif', lineHeight: 1 }}>240+</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Projects</p>
              </div>
              <div
                className="float-card flex-1 rounded-2xl p-5 text-center"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}
              >
                <p style={{ fontSize: '28px', fontWeight: 800, color: '#fff', fontFamily: '"DM Sans",sans-serif', lineHeight: 1 }}>8+</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Years</p>
              </div>
              <div
                className="float-card-delay flex-1 rounded-2xl p-5 text-center"
                style={{ background: '#1F3A8A', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <p style={{ fontSize: '28px', fontWeight: 800, color: '#fff', fontFamily: '"DM Sans",sans-serif', lineHeight: 1 }}>🌍</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Global</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span style={{ fontSize: '10px', fontWeight: 600, color: '#fff', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Scroll</span>
        <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.4)' }} />
      </div>
    </section>
  );
}